import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, Box,
    Typography, IconButton, TextField, InputAdornment, Link, Tooltip, LinearProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { buildCatalogNodes, descendantsOf, ALL_CATALOG_KEYS, hasGrant, expandLegacyGrants, DATA_TAB_KEYS, DATA_SCREEN_KEY } from './permissionCatalog';
import { roleColor } from './permissionsConfig';

// Super-user role manager. Three panes: roles (with CRUD) | permission tree |
// a live summary of the selected role. The tree is DERIVED from the search
// index (permissionCatalog), so new gateable nodes appear automatically. Owner
// is always full access. Interim store is localStorage (PermissionsProvider).

const KIND_ICON = { screen: DashboardOutlinedIcon, tab: FolderOutlinedIcon, group: FolderOpenOutlinedIcon, card: ArticleOutlinedIcon, field: TuneOutlinedIcon };
const ROOT = '__root__';

const RoleDot = ({ k, size = 10 }) => (
    <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: roleColor(k), flexShrink: 0 }} />
);
RoleDot.propTypes = { k: PropTypes.string, size: PropTypes.number };

const RolesManagerDialog = ({ open, onClose, roleDefs, onChangeRole, onAddRole, onRenameRole, onDeleteRole, onDuplicateRole, t }) => {
    const tr = (key, fb) => (typeof t === 'function' ? t(key, fb) : fb);
    const KIND_LABEL = {
        screen: tr('roles_kind_screen', 'Scherm'), tab: tr('roles_kind_tab', 'Tab'),
        group: tr('roles_kind_group', 'Sectie'), card: tr('roles_kind_card', 'Kaart'), field: tr('roles_kind_field', 'Veld'),
    };
    const KINDS = [
        { kind: 'screen', label: tr('roles_kinds_screens', 'Schermen') },
        { kind: 'tab',    label: tr('roles_kinds_tabs', 'Tabs') },
        { kind: 'group',  label: tr('roles_kinds_groups', 'Secties') },
        { kind: 'card',   label: tr('roles_kinds_cards', 'Kaarten') },
        { kind: 'field',  label: tr('roles_kinds_fields', 'Velden') },
    ];
    const nodes = useMemo(() => buildCatalogNodes(t), [t]);
    const byParent = useMemo(() => {
        const m = {};
        nodes.forEach(n => { const p = n.parentKey || ROOT; (m[p] = m[p] || []).push(n); });
        return m;
    }, [nodes]);
    const childrenOf = (key) => byParent[key] || [];

    const [role, setRole] = useState(() => {
        const firstEditable = Object.entries(roleDefs).find(([, d]) => d.allow !== '*');
        return firstEditable ? firstEditable[0] : Object.keys(roleDefs)[0];
    });
    const [filter, setFilter] = useState('');
    const [collapsed, setCollapsed] = useState(() => new Set());
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);

    const activeKey = roleDefs[role] ? role : Object.keys(roleDefs)[0];
    const def = roleDefs[activeKey];
    const isOwnerLike = def && def.allow === '*';
    const allow = useMemo(() => new Set(def && Array.isArray(def.allow) ? def.allow : []), [def]);

    const totalNodes = ALL_CATALOG_KEYS.length;
    // counts go through hasGrant: an old "whole Data screen" grant counts as all its tabs
    const grantedTotal = ALL_CATALOG_KEYS.filter(k => hasGrant(allow, k)).length;
    const grantedForDef = (d) => {
        if (d.allow === '*') return null;
        const set = new Set(d.allow || []);
        return ALL_CATALOG_KEYS.filter(k => hasGrant(set, k)).length;
    };

    const nodeState = (key) => {
        const kids = childrenOf(key);
        if (kids.length === 0) return hasGrant(allow, key) ? 'on' : 'off';
        const states = kids.map(k => nodeState(k.key));
        if (states.every(s => s === 'on')) return 'on';
        if (states.every(s => s === 'off') && !allow.has(key)) return 'off';
        return 'mixed';
    };
    const setBranch = (key, on) => {
        const keys = [key, ...descendantsOf(key)];
        // an old "whole Data screen" grant first becomes its tabs, so
        // unchecking one tab keeps the others
        const next = expandLegacyGrants(allow);
        keys.forEach(k => (on ? next.add(k) : next.delete(k)));
        // no Data tab left = no Data screen (never store the screen alone:
        // that would read as the old "all tabs" grant)
        if (!DATA_TAB_KEYS.some(k => next.has(k))) next.delete(DATA_SCREEN_KEY);
        onChangeRole(activeKey, Array.from(next));
    };
    const toggle = (key) => setBranch(key, nodeState(key) !== 'on');
    const setAll = (on) => onChangeRole(activeKey, on ? [...ALL_CATALOG_KEYS] : []);

    const q = filter.trim().toLowerCase();
    const selfMatch = (n) => !q || (n.label || '').toLowerCase().includes(q);
    const branchMatch = (n) => selfMatch(n) || childrenOf(n.key).some(branchMatch);
    const toggleCollapse = (key) => setCollapsed(prev => {
        const s = new Set(prev); if (s.has(key)) s.delete(key); else s.add(key); return s;
    });

    // ── Role CRUD ────────────────────────────────────────────────────────────
    const startRename = (key) => { setEditingKey(key); setEditValue(roleDefs[key]?.label || ''); };
    const commitRename = () => {
        if (editingKey && editValue.trim()) onRenameRole(editingKey, editValue.trim());
        setEditingKey(null);
    };
    const handleAdd = () => { const key = onAddRole(tr('roles_new_role', 'Nieuwe rol')); if (key) { setRole(key); startRename(key); } };
    const handleDuplicate = (key) => { const nk = onDuplicateRole(key); if (nk) setRole(nk); };
    const confirmDelete = () => {
        const key = pendingDelete;
        if (key) {
            if (activeKey === key) setRole(Object.keys(roleDefs).find(k => k !== key) || 'owner');
            onDeleteRole(key);
        }
        setPendingDelete(null);
    };

    const renderNode = (n) => {
        if (!branchMatch(n)) return null;
        const kids = childrenOf(n.key);
        const hasKids = kids.length > 0;
        const isCollapsed = !q && collapsed.has(n.key);
        const st = nodeState(n.key);
        const Icon = KIND_ICON[n.kind];
        const desc = hasKids ? descendantsOf(n.key) : [];
        const grantedDesc = desc.filter(k => hasGrant(allow, k)).length;
        return (
            <Box key={n.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pr: 1, py: 0.25, borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}>
                    {hasKids ? (
                        <IconButton size="small" onClick={() => toggleCollapse(n.key)} sx={{ p: 0.25 }}>
                            {isCollapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                    ) : <Box sx={{ width: 30, flexShrink: 0 }} />}
                    <Checkbox size="small" checked={st === 'on'} indeterminate={st === 'mixed'} disabled={isOwnerLike} onChange={() => toggle(n.key)} sx={{ p: 0.5 }} />
                    {Icon && (
                        <Tooltip title={KIND_LABEL[n.kind] || ''} placement="top" arrow>
                            <Icon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                        </Tooltip>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: (n.kind === 'screen' || n.kind === 'tab') ? 600 : (n.kind === 'group' ? 500 : 400), flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {n.label}
                    </Typography>
                    {hasKids && (
                        <Typography variant="caption" sx={{ color: grantedDesc ? 'primary.main' : 'text.disabled', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                            {grantedDesc}/{desc.length}
                        </Typography>
                    )}
                </Box>
                {hasKids && !isCollapsed && (
                    <Box sx={{ ml: 2.25, borderLeft: '1px solid', borderColor: 'divider' }}>
                        {kids.map(renderNode)}
                    </Box>
                )}
            </Box>
        );
    };

    const hidden = nodes.filter(n => !hasGrant(allow, n.key));
    const visibleRoots = childrenOf(ROOT).filter(branchMatch);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { width: '96vw', maxWidth: '96vw', height: '90vh' } }}>
                <DialogTitle sx={{ pb: 0.5, pr: 6, position: 'relative' }}>
                    <Typography component="div" variant="h6" sx={{ fontWeight: 700 }}>{tr('roles_title', 'Rollen beheren')}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{tr('roles_subtitle', 'Bepaal per rol wat zichtbaar is in de webshop-admin.')}</Typography>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0 }}>

                    {/* ── Left: roles + CRUD ─────────────────────────────── */}
                    <Box sx={{ width: 260, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Typography variant="overline" sx={{ px: 2, pt: 1, color: 'text.secondary' }}>{tr('roles_list_header', 'Rollen')}</Typography>
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                            {Object.entries(roleDefs).map(([key, d]) => {
                                const selected = key === activeKey;
                                const g = grantedForDef(d);
                                const editable = d.allow !== '*';
                                return (
                                    <Box
                                        key={key}
                                        onClick={() => editingKey !== key && setRole(key)}
                                        sx={{
                                            px: 2, py: 1, cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            borderLeft: '3px solid', borderColor: selected ? 'primary.main' : 'transparent',
                                            backgroundColor: selected ? 'action.selected' : 'transparent',
                                            '&:hover': { backgroundColor: selected ? 'action.selected' : 'action.hover' },
                                            '&:hover .role-actions': { opacity: 1 },
                                        }}
                                    >
                                        <RoleDot k={key} />
                                        {editingKey === key ? (
                                            <TextField
                                                size="small" autoFocus value={editValue} sx={{ flex: 1 }}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onBlur={commitRename}
                                                onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingKey(null); }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {!editable && <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />}
                                                        <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500, lineHeight: 1.25, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {d.label}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {g === null ? tr('roles_full_access', 'Volledige toegang') : `${g} ${tr('roles_of', 'van')} ${totalNodes} ${tr('roles_parts', 'onderdelen')}`}
                                                    </Typography>
                                                </Box>
                                                {editable && (
                                                    <Box className="role-actions" sx={{ display: 'flex', flexShrink: 0, opacity: 0, transition: 'opacity 120ms' }}>
                                                        <Tooltip title={tr('roles_rename', 'Hernoemen')}><IconButton size="small" onClick={(e) => { e.stopPropagation(); startRename(key); }} sx={{ p: 0.5 }}><EditOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                                        <Tooltip title={tr('roles_duplicate', 'Dupliceren')}><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDuplicate(key); }} sx={{ p: 0.5 }}><ContentCopyOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                                        <Tooltip title={tr('roles_delete', 'Verwijderen')}><IconButton size="small" onClick={(e) => { e.stopPropagation(); setPendingDelete(key); }} sx={{ p: 0.5 }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                        <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Button fullWidth size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ textTransform: 'none' }}>{tr('roles_new_role', 'Nieuwe rol')}</Button>
                        </Box>
                    </Box>

                    {/* ── Middle: permission tree ────────────────────────── */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider' }}>
                        {isOwnerLike ? (
                            <Box sx={{ m: 'auto', textAlign: 'center', color: 'text.secondary', p: 4 }}>
                                <LockOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2">{tr('roles_owner_locked', 'Deze rol heeft altijd volledige toegang en is niet beperkbaar.')}</Typography>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <RoleDot k={activeKey} size={12} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{def.label}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{grantedTotal} {tr('roles_of', 'van')} {totalNodes} {tr('roles_parts_allowed', 'onderdelen toegestaan')}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                                        <Link component="button" type="button" underline="hover" sx={{ fontSize: '0.8rem' }} onClick={() => setAll(true)}>{tr('roles_all_on', 'Alles aan')}</Link>
                                        <Link component="button" type="button" underline="hover" sx={{ fontSize: '0.8rem', color: 'text.secondary' }} onClick={() => setAll(false)}>{tr('roles_all_off', 'Alles uit')}</Link>
                                    </Box>
                                </Box>
                                <Box sx={{ px: 2.5, pb: 1 }}>
                                    <TextField size="small" fullWidth placeholder={tr('roles_filter_placeholder', 'Filter op naam…')} value={filter} onChange={(e) => setFilter(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                                </Box>
                                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pb: 1.5 }}>
                                    {(q && visibleRoots.length === 0)
                                        ? <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">{tr('roles_no_results', 'Geen resultaten voor')} “{filter}”.</Typography></Box>
                                        : visibleRoots.map(renderNode)}
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* ── Right: summary for the selected role ───────────── */}
                    <Box sx={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Typography variant="overline" sx={{ px: 2, pt: 1, color: 'text.secondary' }}>{tr('roles_summary', 'Samenvatting')}</Typography>
                        {isOwnerLike ? (
                            <Box sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                                <Typography variant="body2">{tr('roles_full_access_all', 'Volledige toegang tot alles.')}</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.5 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{grantedTotal}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>/ {totalNodes} {tr('roles_parts', 'onderdelen')}</Typography>
                                </Box>
                                {KINDS.map(({ kind, label }) => {
                                    const list = nodes.filter(n => n.kind === kind);
                                    if (!list.length) return null;
                                    const g = list.filter(n => hasGrant(allow, n.key)).length;
                                    return (
                                        <Box key={kind} sx={{ mb: 1.25 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{g}/{list.length}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={list.length ? (g / list.length) * 100 : 0} sx={{ height: 6, borderRadius: 3 }} />
                                        </Box>
                                    );
                                })}
                                <Typography variant="overline" sx={{ display: 'block', mt: 2, mb: 0.5, color: 'text.secondary' }}>
                                    {tr('roles_hidden', 'Verborgen')} ({hidden.length})
                                </Typography>
                                {hidden.length === 0 ? (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('roles_nothing_hidden', 'Niets verborgen — deze rol ziet alles.')}</Typography>
                                ) : hidden.map(n => {
                                    const Icon = KIND_ICON[n.kind];
                                    return (
                                        <Box key={n.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                                            {Icon && <Icon fontSize="small" sx={{ color: 'text.disabled' }} />}
                                            <Typography variant="caption" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.label}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 2.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {tr('roles_footer_note', 'Wijzigingen gelden meteen, ook in de simulatie, en worden gedeeld met alle beheerders.')}
                    </Typography>
                    <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>{tr('roles_close', 'Sluiten')}</Button>
                </DialogActions>
            </Dialog>

            {/* In-modal delete confirmation (replaces window.confirm). */}
            <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>{tr('roles_delete_title', 'Rol verwijderen?')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        “{roleDefs[pendingDelete]?.label || ''}” {tr('roles_delete_body', 'wordt verwijderd. Dit kan niet ongedaan gemaakt worden.')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)} sx={{ textTransform: 'none' }}>{tr('roles_cancel', 'Annuleer')}</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete} sx={{ textTransform: 'none' }}>{tr('roles_delete', 'Verwijderen')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

RolesManagerDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    roleDefs: PropTypes.object,
    onChangeRole: PropTypes.func,
    onAddRole: PropTypes.func,
    onRenameRole: PropTypes.func,
    onDeleteRole: PropTypes.func,
    onDuplicateRole: PropTypes.func,
    t: PropTypes.func,
};

export default RolesManagerDialog;
